import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { AssetsAPI } from "@/lib/api";

const KINDS = [
  { id: "competitor", label: "Competitor" },
  { id: "previous_pack", label: "Previous pack" },
  { id: "reference", label: "Reference" },
  { id: "category_benchmark", label: "Category benchmark" },
  { id: "subject_pack", label: "Subject pack" },
];

const MAX_BYTES = 12 * 1024 * 1024;
const ACCEPT = { "image/jpeg": [], "image/png": [], "image/webp": [] };

export default function Library({ projectId, project }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [defaultKind, setDefaultKind] = useState("competitor");
  const [url, setUrl] = useState("");
  const [urlLabel, setUrlLabel] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await AssetsAPI.list(projectId);
      setAssets(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const onDrop = useCallback(async (accepted, rejected) => {
    setError("");
    if (rejected?.length) setError("Only JPEG / PNG / WEBP under 12MB are accepted.");
    if (!accepted?.length) return;
    setBusy(true);
    try {
      const items = await Promise.all(accepted.map(async (f) => ({
        data_b64: await fileToBase64(f),
        mime: f.type,
        label: f.name,
        kind: defaultKind,
      })));
      await AssetsAPI.upload(projectId, items);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [projectId, defaultKind]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: ACCEPT, maxSize: MAX_BYTES });

  const addFromUrl = async () => {
    if (!url) return;
    setBusy(true); setError("");
    try {
      await AssetsAPI.fromUrl(projectId, { url, label: urlLabel, kind: defaultKind });
      setUrl(""); setUrlLabel("");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (asset_id) => {
    if (!window.confirm("Delete this asset?")) return;
    await AssetsAPI.remove(projectId, asset_id);
    setAssets((xs) => xs.filter((a) => a.asset_id !== asset_id));
  };

  const updateKind = async (asset_id, kind) => {
    await AssetsAPI.patch(projectId, asset_id, { kind });
    setAssets((xs) => xs.map((a) => a.asset_id === asset_id ? { ...a, kind } : a));
  };

  const updateLabel = async (asset_id, label) => {
    await AssetsAPI.patch(projectId, asset_id, { label });
    setAssets((xs) => xs.map((a) => a.asset_id === asset_id ? { ...a, label } : a));
  };

  const grouped = useMemo(() => {
    const filtered = filter === "all" ? assets : assets.filter((a) => a.kind === filter);
    return filtered;
  }, [assets, filter]);

  const counts = useMemo(() => {
    const c = { all: assets.length };
    KINDS.forEach((k) => { c[k.id] = assets.filter((a) => a.kind === k.id).length; });
    return c;
  }, [assets]);

  const seedUrls = project?.competitor_urls || [];

  return (
    <div data-testid="library-panel">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="overline">Project library</div>
          <h2 style={{ fontSize: 28, marginTop: 6 }}>Asset repository</h2>
          <p style={{ color: "var(--v-text-2)", fontSize: 14, marginTop: 6, maxWidth: 640 }}>
            Every pack and reference image for this brand lives here. Use them in any assessment without re-uploading.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[{ id: "all", label: "All" }, ...KINDS].map((k) => (
            <button
              key={k.id}
              data-testid={`library-filter-${k.id}`}
              onClick={() => setFilter(k.id)}
              className={filter === k.id ? "v-btn" : "v-btn v-btn-ghost"}
              style={{ padding: "8px 14px", fontSize: 11 }}
            >
              {k.label} <span style={{ marginLeft: 6, opacity: .65 }}>{counts[k.id] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ marginTop: 28 }}>
        <div className="md:col-span-2 v-surface" style={{ padding: 20 }}>
          <div className="overline">Default kind for new assets</div>
          <select
            className="v-select"
            value={defaultKind}
            onChange={(e) => setDefaultKind(e.target.value)}
            data-testid="library-default-kind"
            style={{ marginTop: 8, width: "auto", maxWidth: 260 }}
          >
            {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
          <div {...getRootProps()} className={`v-dropzone ${isDragActive ? "is-drag" : ""}`} data-testid="library-dropzone" style={{ marginTop: 14 }}>
            <input {...getInputProps()} />
            <div className="overline" style={{ color: "var(--v-text)" }}>{busy ? "Uploading…" : "Drop images or click to browse"}</div>
            <p style={{ marginTop: 6, color: "var(--v-text-2)", fontSize: 13 }}>JPEG / PNG / WEBP · up to 12 MB · multi-select supported</p>
          </div>
        </div>

        <div className="v-surface" style={{ padding: 20 }}>
          <div className="overline">Add by URL</div>
          <input className="v-input" placeholder="https://amazon.in/…/pack.jpg" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="library-url-input" style={{ marginTop: 8 }} />
          <input className="v-input" placeholder="Label (optional)" value={urlLabel} onChange={(e) => setUrlLabel(e.target.value)} data-testid="library-url-label" style={{ marginTop: 8 }} />
          <button className="v-btn" onClick={addFromUrl} disabled={busy || !url} data-testid="library-url-add-btn" style={{ marginTop: 10, width: "100%" }}>
            {busy ? "Fetching…" : "Fetch & add"}
          </button>
          {seedUrls.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="overline" style={{ fontSize: 9 }}>From brief</div>
              {seedUrls.map((u) => (
                <button
                  key={u}
                  onClick={() => { setUrl(u); }}
                  className="mono"
                  data-testid={`library-seed-url-${u}`}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    background: "transparent", border: "1px dashed var(--v-border)",
                    padding: "6px 10px", marginTop: 6, fontSize: 11,
                    color: "var(--v-text-2)", cursor: "pointer", borderRadius: 4,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}
                >
                  ↳ {u}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <div className="mono" style={{ marginTop: 16, color: "var(--v-accent)", fontSize: 12 }} data-testid="library-error">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" style={{ marginTop: 28 }}>
        {loading && <div className="overline">Loading…</div>}
        {!loading && grouped.length === 0 && (
          <div className="v-surface" style={{ gridColumn: "1 / -1", padding: 28 }} data-testid="library-empty">
            <div className="overline">No assets yet</div>
            <p style={{ marginTop: 6, color: "var(--v-text-2)" }}>Drop competitor packs, previous designs or category references here. They'll be reusable across every assessment in this project.</p>
          </div>
        )}
        {grouped.map((a) => (
          <AssetCard
            key={a.asset_id}
            asset={a}
            projectId={projectId}
            onDelete={() => remove(a.asset_id)}
            onChangeKind={(k) => updateKind(a.asset_id, k)}
            onChangeLabel={(l) => updateLabel(a.asset_id, l)}
          />
        ))}
      </div>
    </div>
  );
}

function AssetCard({ asset, projectId, onDelete, onChangeKind, onChangeLabel }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(asset.label || "");
  return (
    <div className="v-surface" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }} data-testid={`asset-${asset.asset_id}`}>
      <div style={{ position: "relative", aspectRatio: "1/1", background: "var(--v-muted)", overflow: "hidden", borderRadius: 4 }}>
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/assets/${asset.asset_id}?download=1`} alt={asset.label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", top: 6, left: 6 }} className="v-chip">
          {asset.kind.replace("_", " ")}
        </div>
      </div>
      {editing ? (
        <input
          autoFocus
          className="v-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => { setEditing(false); if (label !== asset.label) onChangeLabel(label); }}
          onKeyDown={(e) => { if (e.key === "Enter") { setEditing(false); if (label !== asset.label) onChangeLabel(label); } }}
          data-testid={`asset-label-input-${asset.asset_id}`}
          style={{ fontSize: 12, padding: "6px 8px" }}
        />
      ) : (
        <div onClick={() => setEditing(true)} className="mono" data-testid={`asset-label-${asset.asset_id}`} style={{ fontSize: 11, color: "var(--v-text)", cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={asset.label}>
          {asset.label || "—"}
        </div>
      )}
      <select className="v-select" value={asset.kind} onChange={(e) => onChangeKind(e.target.value)} data-testid={`asset-kind-${asset.asset_id}`} style={{ padding: "6px 8px", fontSize: 11 }}>
        {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
      </select>
      <div className="mono" style={{ fontSize: 10, color: "var(--v-text-3)" }}>
        {asset.source === "url" ? "url" : "upload"} · {Math.round((asset.size_bytes || 0) / 1024)} KB
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <a className="v-btn v-btn-ghost" href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/assets/${asset.asset_id}?download=1`} target="_blank" rel="noreferrer" data-testid={`asset-download-${asset.asset_id}`} style={{ padding: "6px 10px", fontSize: 10, flex: 1 }}>
          Download
        </a>
        <button className="v-btn v-btn-ghost" onClick={onDelete} data-testid={`asset-delete-${asset.asset_id}`} style={{ padding: "6px 10px", fontSize: 10, flex: 1, borderColor: "var(--v-border)" }}>
          Delete
        </button>
      </div>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = r.result || "";
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
